import { useCallback, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { AuthContext } from '../auth/AuthContext';
import api from '../utils/api';
import { withOfflineFallback } from '../offline/offlineQuery';
import { readClinic } from '../offline/readers';
import type { components } from '../types/api.generated';
import {
  DEFAULT_CLINIC_TIMEZONE,
  INSTANT_FORMATS,
  browserTimeZone,
  formatInstantInZone,
} from '../utils/dateUtils';

/**
 * The clinic's IANA timezone, for rendering INSTANTS.
 *
 * Why this exists (Docs/architecture/DATE-KINDS.md §3.5): 85 of the 140
 * baselined date-idiom sites render a real moment in the VIEWER'S browser zone
 * instead of the clinic's. None of them could be fixed, because the web app had
 * no reader for `Clinic.timezone` and no formatter that takes one — even though
 * `GET /clinics/current` is un-narrowed and has been shipping the value all
 * along (eight pages receive it and discard it). This hook plus
 * `formatInstantInZone` are the two halves that unblock those 85.
 *
 * ⚠ Instants only. A calendar day (DOB, due date, service date) must NOT be
 * re-zoned into anything — see `formatCalendarDate`.
 */

/**
 * The shared React Query key for the clinic record.
 *
 * Two readers still fetch `/clinics/current` on their own
 * (`['clinic','current','label-test']` in PrintersSettings, a bare `useEffect` in
 * InvoiceDetail). Every clinic save invalidates the `['clinic']` prefix, which
 * covers this key and the settings pages' `['clinic', clinicId]`.
 */
export const CLINIC_QUERY_KEY = ['clinic', 'current'] as const;

/**
 * GET /clinics/current, typed from the server's response class (review fix 36), so a field the
 * server stops sending breaks this build. Partial because the offline fallback is the sync
 * replica's row, which lacks the REST-only keys. `timezoneSetAt` NULL means UNKNOWN, not Eastern:
 * `timezone` has a schema default, so it cannot answer that on its own (ADR-0034).
 */
export type ClinicRecord = Partial<components['schemas']['ClinicResponseDto']>;

/**
 * The zone to RENDER instants in: the clinic's once somebody has set it, the
 * viewer's browser until then.
 *
 * Pure, and both zones are arguments, so the two branches are testable without
 * touching the host clock or `Intl`.
 *
 * WHY NOT JUST `clinic.timezone`. It is `@default("America/New_York")`, so every
 * clinic that has never opened Settings → Practice reads as Eastern with total
 * confidence. Before PR #647 that cost nothing — the web app did not read the
 * column. It does now, so the default would put Eastern wall clocks on a Pacific
 * clinic's own screens, where the browser zone had been accidentally right for
 * staff standing in the clinic. The honest states are KNOWN and UNKNOWN, and
 * this is where they diverge. Once the clinic's first OWNER/ADMIN logs in, the
 * auto-detect stamps it and this returns the clinic zone forever after.
 */
export function pickDisplayTimezone(
  clinic: Pick<ClinicRecord, 'timezone' | 'timezoneSetAt'> | undefined,
  browserZone: string,
): string {
  const known = !!clinic?.timezoneSetAt;
  const tz = clinic?.timezone;
  if (known) {
    // KNOWN, so the browser is out of it — a blank column falls to the schema
    // default rather than back to the viewer, which would silently un-know it.
    return typeof tz === 'string' && tz.trim() ? tz : DEFAULT_CLINIC_TIMEZONE;
  }
  // Unknown: the viewer's own zone. A blank browser zone (locked-down `Intl`)
  // degrades to the schema default rather than to an empty string, which would
  // make `formatInstantInZone` throw its way to the same place more slowly.
  return browserZone.trim() || DEFAULT_CLINIC_TIMEZONE;
}

/**
 * The clinic record, cached app-wide. Offline-capable: falls back to the synced
 * replica row (`sync.service.ts` ships `timezone` in the clinic payload).
 *
 * `staleTime` is deliberately long — a clinic's timezone/letterhead does not
 * change mid-session, and this hook is meant to be called from many components
 * at once without any of them triggering a fetch.
 */
export function useClinicRecord(options: { staleTime?: number } = {}) {
  const { user } = useContext(AuthContext);
  return useQuery<ClinicRecord | undefined>(
    CLINIC_QUERY_KEY,
    withOfflineFallback(
      () => api.get('/clinics/current').then((r) => r.data),
      () => readClinic() as Promise<ClinicRecord | undefined>,
    ),
    {
      // Unauthenticated routes (login, verify-pending) must not fire a 401.
      enabled: !!user,
      staleTime: options.staleTime ?? 30 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
      retry: false,
      // A missing clinic must degrade to the schema default, never to an error
      // boundary — a date render is not worth blanking a clinical screen.
      onError: () => undefined,
    },
  );
}

/**
 * The zone to render INSTANTS in — the clinic's once it is KNOWN
 * (`timezoneSetAt` is set), the viewer's browser zone until then. Never returns
 * undefined, so a caller never has to branch. See `pickDisplayTimezone` for why
 * a set `timezone` is not by itself an answer.
 */
export function useClinicTimezone(): string {
  const { data } = useClinicRecord();
  const tz = data?.timezone;
  const setAt = data?.timezoneSetAt;
  return useMemo(
    () => pickDisplayTimezone({ timezone: tz, timezoneSetAt: setAt }, browserTimeZone()),
    [tz, setAt],
  );
}

/**
 * `formatInstantInZone` with the clinic's zone already bound — the convenience
 * a component actually wants:
 *
 * ```tsx
 * const formatInstant = useFormatInstant();
 * <span>{formatInstant(soapNote.completedAt)}</span>
 * <span>{formatInstant(appointment.date, INSTANT_FORMATS.time)}</span>
 * ```
 *
 * Accepts a `string` (the ISO on the wire) or a `Date` (already-parsed values
 * such as an appointment's date object) — the same shape either way.
 *
 * ⚠ Instants only, exactly like the helper it wraps.
 */
export function useFormatInstant(): (
  value: string | Date | null | undefined,
  pattern?: string,
) => string {
  const tz = useClinicTimezone();
  return useCallback(
    (value: string | Date | null | undefined, pattern: string = INSTANT_FORMATS.dateTime) =>
      formatInstantInZone(value, tz, pattern),
    [tz],
  );
}
